import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getOperationLogs,
  type OperationLog,
  type OpType,
  type UserRole,
  type LogStatus,
} from "@/services/operationLogs";
import { getRoleColor } from "@/constants/roleColors";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import AdminDataTable from "@/components/admin/AdminDataTable";
import AdminUserCell from "@/components/admin/AdminUserCell";
import {
  AdminSearchForm,
  AdminSelectFilter,
  AdminTableToolbar,
} from "@/components/admin/AdminTableToolbar";

const OP_TYPE_STYLE = { bg: "rgba(131,0,81,0.08)", color: "#830051" };

const OP_TYPE_OPTIONS: OpType[] = [
  "login",
  "logout",
  "submitFeedback",
  "translate",
  "feedback",
  "downloadSource",
  "uploadPersonnelFiles",
  "manageUser",
  "manageStudy",
  "manageAskSource",
  "manageMvrSource",
  "markFailedFiles",
  "manageRecommendedQuestion",
  "manageHealthMonitor",
  "manageSecurityRecords",
  "setFeedbackNote",
  "manageSystemSettings",
];
const ROLE_OPTIONS: UserRole[] = ["CRA", "CSM", "Admin", "Staff", "Auditor", "Manager"];

function formatIpAddress(ip: string) {
  if (ip === "::1") return "-";
  return ip.startsWith("::ffff:") ? ip.slice("::ffff:".length) : ip;
}

interface DetailDrawerProps {
  log: OperationLog;
  onClose: () => void;
}

function DetailModal({ log, onClose }: DetailDrawerProps) {
  const { t } = useTranslation();
  const opStyle = OP_TYPE_STYLE;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.3)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <span className="text-base font-semibold text-gray-900">
            {t("systemLogs.detail.title")}
          </span>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg px-3 py-2.5">
              <div className="text-xs text-gray-400 mb-0.5">{t("systemLogs.col.time")}</div>
              <div className="text-sm font-medium text-gray-700">{log.time}</div>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2.5">
              <div className="text-xs text-gray-400">{t("systemLogs.col.user")}</div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{log.user}</span>
                {(() => {
                  const rc = getRoleColor(log.role);
                  return (
                    <span
                      className="px-1.5 py-0.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: rc.bg, color: rc.color }}
                    >
                      {log.role}
                    </span>
                  );
                })()}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2.5">
              <div className="text-xs text-gray-400 mb-0.5">{t("systemLogs.col.ip")}</div>
              <div className="text-sm font-medium text-gray-700">{formatIpAddress(log.ip)}</div>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2.5">
              <div className="text-xs text-gray-400 mb-0.5">{t("systemLogs.col.status")}</div>
              <div className="text-sm font-medium text-gray-700">
                {log.status === "success"
                  ? t("systemLogs.status.success")
                  : t("systemLogs.status.failed")}
              </div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg px-3 py-2.5">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-xs text-gray-400">{t("systemLogs.col.opType")}</span>
              <span
                className="px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: opStyle.bg, color: opStyle.color }}
              >
                {t(`systemLogs.op.${log.opType}`)}
              </span>
            </div>
            <div className="text-sm text-gray-700">{log.detail}</div>
          </div>
          {log.params && Object.keys(log.params).length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-2">
                {t("systemLogs.detail.params")}
              </div>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                {Object.entries(log.params)
                  .filter(([k]) => {
                    if (log.params?.action === "Edit") {
                      return !["addedPaths", "removedPaths"].includes(k);
                    }
                    return true;
                  })
                  .map(([k, v]) => (
                    <div key={k} className="flex items-start gap-2 text-xs">
                      <span className="text-gray-400 shrink-0">{k}:</span>
                      <span className="text-gray-700 break-all">{v}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OperationLogsTab() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [opTypeFilter, setOpTypeFilter] = useState<OpType | "">("");
  const [roleFilter, setRoleFilter] = useState<UserRole | "">("");
  const [statusFilter, setStatusFilter] = useState<"" | LogStatus>("");
  const [selectedLog, setSelectedLog] = useState<OperationLog | null>(null);

  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [search, opTypeFilter, roleFilter, statusFilter]);

  // 是否存在生效的筛选/搜索条件，用于控制 Reset 按钮显示
  const hasActiveFilters =
    search !== "" ||
    searchInput !== "" ||
    opTypeFilter !== "" ||
    roleFilter !== "" ||
    statusFilter !== "";

  // 提交搜索：将草稿写入正式搜索状态
  const handleSearch = () => {
    setSearch(searchInput);
  };

  // 重置全部筛选与搜索条件
  const handleReset = () => {
    setSearch("");
    setSearchInput("");
    setOpTypeFilter("");
    setRoleFilter("");
    setStatusFilter("");
  };

  // 服务端筛选：操作类型/角色/状态即时生效，搜索框做 300ms 防抖。
  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(() => {
      setLoading(true);
      getOperationLogs({
        opType: opTypeFilter,
        role: roleFilter,
        status: statusFilter,
        search,
        page,
        pageSize,
      })
        .then((res) => {
          if (!cancelled) {
            setLogs(res.data);
            setTotal(res.total);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setLogs([]);
            setTotal(0);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search, opTypeFilter, roleFilter, statusFilter, page, pageSize]);

  const filtered = logs;

  function formatDateTime(iso: string) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: "", time: "" };
    const pad = (n: number) => String(n).padStart(2, "0");
    const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return { date, time };
  }

  return (
    <AdminLayout>
      {/* Filters */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <AdminPageTitle
            title="Operation Logs"
            tooltip="Records all front-end user and admin operations, including login, logout, questions, source downloads, feedback submissions, and admin actions"
          />
        </div>
      </div>

      <AdminTableToolbar>
        <AdminSelectFilter
          label={t("systemLogs.filter.opType")}
          value={opTypeFilter}
          onChange={(value) => setOpTypeFilter(value as OpType | "")}
          options={[
            { value: "", label: t("systemLogs.filter.allOpTypes") },
            ...OP_TYPE_OPTIONS.map((op) => ({
              value: op,
              label: t(`systemLogs.op.${op}`),
            })),
          ]}
        />
        <AdminSelectFilter
          label={t("systemLogs.filter.role")}
          value={roleFilter}
          onChange={(value) => setRoleFilter(value as UserRole | "")}
          options={[
            { value: "", label: t("systemLogs.filter.allRoles") },
            ...ROLE_OPTIONS.map((role) => ({ value: role, label: role })),
          ]}
        />
        <AdminSelectFilter
          label={t("systemLogs.filter.status")}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as "" | "success" | "failed")}
          options={[
            { value: "", label: t("systemLogs.filter.allStatus") },
            { value: "success", label: t("systemLogs.status.success") },
            { value: "failed", label: t("systemLogs.status.failed") },
          ]}
        />
        <AdminSearchForm
          value={searchInput}
          onChange={setSearchInput}
          onSearch={handleSearch}
          searchLabel={t("common.search")}
          placeholder={t("systemLogs.filter.search")}
          showReset={hasActiveFilters}
          onReset={handleReset}
          resetLabel={t("systemLogs.filter.reset")}
        />
      </AdminTableToolbar>

      <AdminDataTable
        headers={[
          t("systemLogs.col.time"),
          t("systemLogs.col.user"),
          // t("systemLogs.col.role"),
          t("systemLogs.col.opType"),
          t("systemLogs.col.detail"),
          t("systemLogs.col.ip"),
          t("systemLogs.col.status"),
          "Action",
        ]}
        columnWidths={["10%", "18%", "13%", "30%", "13%", "8%", "8%"]}
        colSpan={8}
        loading={loading}
        empty={filtered.length === 0}
        emptyText={t("systemLogs.noLogs")}
        emptyHint={t("users.table.noResultsHint")}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={(nextPage, nextPageSize) => {
          setPage(nextPage);
          setPageSize(nextPageSize);
        }}
      >
        {filtered.map((log) => {
          const roleColor = getRoleColor(log.role);
          const { date, time } = formatDateTime(log.time);

          return (
            <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
              <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{date}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{time}</div>
                </div>
              </td>
              <td className="px-4 py-4 align-top">
                <AdminUserCell name={log.user} userId={log.userId} />
              </td>
              {/* <td className="px-3 py-2.5 whitespace-nowrap">
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: roleColor.bg, color: roleColor.color }}
                >
                  {log.role}
                </span>
              </td> */}
              <td className="px-3 py-2.5 whitespace-nowrap overflow-hidden">
                <span
                  className="inline-block max-w-full truncate align-middle px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: OP_TYPE_STYLE.bg, color: OP_TYPE_STYLE.color }}
                  title={t(`systemLogs.op.${log.opType}`)}
                >
                  {t(`systemLogs.op.${log.opType}`)}
                </span>
              </td>
              <td className="px-3 py-2.5 text-gray-600 truncate" title={log.detail}>
                {log.detail}
              </td>
              <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">
                {formatIpAddress(log.ip)}
              </td>
              <td className="px-3 py-2.5 whitespace-nowrap">
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.status === "success" ? "text-green-700 bg-green-50" : "text-red-600 bg-red-50"}`}
                >
                  {log.status === "success"
                    ? t("systemLogs.status.success")
                    : t("systemLogs.status.failed")}
                </span>
              </td>
              <td className="px-3 py-2.5">
                <button
                  onClick={() => setSelectedLog(log)}
                  className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-[#830051] cursor-pointer transition-colors"
                  title={t("systemLogs.viewDetail")}
                >
                  <i className="ri-eye-line text-sm"></i>
                </button>
              </td>
            </tr>
          );
        })}
      </AdminDataTable>

      {selectedLog && <DetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />}
    </AdminLayout>
  );
}
